import { isValidElement, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "@entry/navigation";
import styled from "styled-components";

import { ButtonProps } from "@components/Button/types";
import { BottomButtonProps, OnboardingLayoutProps } from "@components/OnboardingLayout/types";
import { StorageKey } from "@ctypes";
import { Analytics, Intercom } from "@services";
import { getRouteForPath } from "@utils";
import { entryFont } from "./tokens";
import { EntryCta, EntryTitle, FormColumn, Spinner } from "./ui";
import { useEntryAnimation } from "./useEntryAnimation";

const StepScreen = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  padding: 40px 0 0;
`;

const StepColumn = styled(FormColumn)`
  flex: 1;

  /* Top-aligned like mobile: the title sits in the same place on every
     step and fields flow straight down toward the pinned CTA. */
`;

const Subtitle = styled.p`
  margin: 18px 0 0;
  font-family: ${entryFont.body};
  font-size: 15px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.75);
`;

const Children = styled.div`
  margin-top: 26px;
  display: flex;
  flex-direction: column;
`;

const CtaBlock = styled.div`
  /* Pinned to the viewport bottom while its screen scrolls, so the primary
     action is always visible without scrolling (mobile parity). */
  position: sticky;
  bottom: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-top: auto;
  padding: 28px 0 22px;

  /* Full-bleed scrim so scrolling content fades out under the pinned CTA. */
  &::before {
    content: "";
    position: absolute;
    inset: 0 auto 0 50%;
    transform: translateX(-50%);
    width: 100vw;
    z-index: -1;
    background: linear-gradient(
      180deg,
      rgba(9, 11, 31, 0) 0%,
      rgba(9, 11, 31, 0.65) 45%,
      rgba(9, 11, 31, 0.85) 100%
    );
    pointer-events: none;
  }

  /* CTAs line up with the form column (inputs, cards) above them. */
  & > button {
    max-width: 100%;
  }
`;

const FooterBlock = styled.div`
  margin-top: 22px;
`;

const SupportChatBtn = styled.button`
  position: fixed;
  top: 26px;
  right: 26px;
  z-index: 2;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 0;

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.5);
    outline-offset: 2px;
    border-radius: 50%;
  }
`;

const SupportChatIcon = () => (
  <svg width="44" height="44" viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <path
      d="M0 24C0 10.7452 10.7452 0 24 0C37.2548 0 48 10.7452 48 24C48 37.2548 37.2548 48 24 48C10.7452 48 0 37.2548 0 24Z"
      fill="#DF8F68"
      fillOpacity={0.3}
    />
    <path
      d="M24 0.7061C36.865 0.7061 47.2939 11.135 47.2939 24C47.2939 36.865 36.865 47.2939 24 47.2939C11.135 47.2939 0.7061 36.865 0.7061 24C0.7061 11.135 11.135 0.7061 24 0.7061Z"
      stroke="url(#sc_g1)"
      strokeOpacity={0.35}
      strokeWidth={1.41176}
    />
    <path
      d="M24 0.7061C36.865 0.7061 47.2939 11.135 47.2939 24C47.2939 36.865 36.865 47.2939 24 47.2939C11.135 47.2939 0.7061 36.865 0.7061 24C0.7061 11.135 11.135 0.7061 24 0.7061Z"
      stroke="url(#sc_g2)"
      strokeOpacity={0.35}
      strokeWidth={1.41176}
    />
    <g clipPath="url(#sc_clip)">
      <path
        d="M24.6282 13.1976C23.8141 13.1357 23.0627 13.2127 22.2626 13.3373C19.3655 13.7884 16.8275 15.3448 15.1155 17.7338C13.5358 19.9201 12.9077 22.6523 13.3739 25.3089C13.7979 27.8532 15.4641 30.4343 17.5699 31.904C20.1404 33.6982 22.9181 34.2446 25.9633 33.7341C26.822 33.5319 27.3711 33.3672 28.1918 33.0674C28.4252 33.1333 29.017 33.4056 29.2537 33.5103L31.1785 34.3688C31.5314 34.5266 31.8518 34.6808 32.2255 34.788C32.6479 34.9586 33.2976 34.5284 33.1843 34.0529C32.8843 32.7942 32.4649 31.4608 32.1098 30.2175C32.1338 30.1993 32.1573 30.1805 32.1803 30.1611C32.4016 29.9737 32.7522 29.5287 32.9234 29.2945C33.99 27.808 34.6222 26.0542 34.7494 24.2291C35.0303 20.6768 33.1691 17.1449 30.278 15.1406C28.5504 13.943 26.7088 13.3573 24.6282 13.1976Z"
        stroke="#F5F0EB"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18.457 25.2383C19.0949 26.1941 19.9283 26.9719 20.89 27.509C21.8517 28.046 22.9146 28.3271 23.9929 28.3296C25.0712 28.332 26.1345 28.0558 27.097 27.5232C28.0594 26.9906 28.894 26.2166 29.5332 25.2637"
        stroke="#F5F0EB"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <linearGradient id="sc_g1" x1="0" y1="0" x2="18.3529" y2="18.3529" gradientUnits="userSpaceOnUse">
        <stop stopColor="white" />
        <stop offset="1" stopColor="white" stopOpacity={0} />
      </linearGradient>
      <linearGradient id="sc_g2" x1="48" y1="48" x2="29.6471" y2="29.6471" gradientUnits="userSpaceOnUse">
        <stop stopColor="white" />
        <stop offset="1" stopColor="white" stopOpacity={0} />
      </linearGradient>
      <clipPath id="sc_clip">
        <rect width="24" height="24" fill="white" transform="matrix(-1 0 0 1 36 12)" />
      </clipPath>
    </defs>
  </svg>
);

const StepCta = ({
  button,
  variant,
  onPress,
}: {
  button: ButtonProps;
  variant: "green" | "glass";
  onPress: () => void;
}) => {
  const disabled = Boolean(button.disabled) || Boolean(button.loading);

  return (
    <EntryCta
      type="button"
      $variant={variant}
      $disabled={Boolean(button.disabled)}
      disabled={disabled}
      aria-disabled={disabled}
      aria-busy={Boolean(button.loading)}
      onClick={onPress}
    >
      {button.leftIcon}
      {button.loading ? <Spinner /> : button.title}
      {button.rightIcon}
    </EntryCta>
  );
};

/**
 * Entry-styled drop-in for OnboardingLayout: same props, same analytics,
 * rendered inside the persistent EntryShell with the mobile animation
 * grammar. Ported routes swap only their layout import.
 */
export const EntryStepLayout = ({
  title,
  subtitle,
  subtitleStyle,
  cta: ctaProp,
  footer,
  children,
  style,
  analytics = true,
  analyticsName,
  subContent,
  hideLogout = false,
  footerStyle,
  animationVariant,
}: OnboardingLayoutProps & { animationVariant?: "unified" | "sequenced" | "none" }) => {
  const location = useLocation();

  const route = analyticsName ?? getRouteForPath(location.pathname);

  const { contentStyle, formStyle, ctaStyle } = useEntryAnimation({ variant: animationVariant });

  // CtaBlock is sticky with nothing after it, so once stuck it overlaps a
  // constant band the height of the CTA for the rest of the scroll — with
  // no spacer at least that tall reserved above it, the last CTA-height's
  // worth of real content stays hidden underneath it even at max scroll.
  // Measuring the actual rendered height keeps this correct as the CTA's
  // content changes (loading state, wrapped button text, a footer, etc.).
  const ctaRef = useRef<HTMLDivElement>(null);
  const [ctaHeight, setCtaHeight] = useState(0);

  useLayoutEffect(() => {
    const el = ctaRef.current;
    if (!el) {
      setCtaHeight(0);
      return;
    }

    // Observing the element (not re-running this on every prop change) is
    // deliberate: ctaProp is reconstructed inline on most call sites, so
    // depending on it would tear down and recreate the observer on every
    // render (e.g. every keystroke in a form). ResizeObserver already
    // reports every subsequent height change on this same node regardless
    // of what caused it (loading state, disabled state, a footer showing).
    const observer = new ResizeObserver(([entry]) => setCtaHeight(entry.contentRect.height));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!analytics) return;
    Analytics.page(route);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analytics]);

  const hasToken = Boolean(localStorage.getItem(StorageKey.Token));

  const cta = (() => {
    if (!ctaProp) return null;
    if (isValidElement(ctaProp)) {
      return (
        <CtaBlock ref={ctaRef} style={ctaStyle}>
          {ctaProp}
        </CtaBlock>
      );
    }

    const { primary, secondary } = ctaProp as BottomButtonProps;
    if (!primary && !secondary) return null;

    return (
      <CtaBlock ref={ctaRef} style={ctaStyle}>
        {primary ? (
          <StepCta
            button={primary}
            variant="green"
            onPress={() => {
              if (analytics) Analytics.track(`${route} Primary CTA Pressed`);
              primary.onClick?.();
            }}
          />
        ) : null}
        {secondary ? (
          <StepCta
            button={secondary}
            variant="glass"
            onPress={() => {
              if (analytics) Analytics.track(`${route} Secondary CTA Pressed`);
              secondary.onClick?.();
            }}
          />
        ) : null}
        {footer ? <FooterBlock style={footerStyle}>{footer}</FooterBlock> : null}
      </CtaBlock>
    );
  })();

  return (
    <StepScreen style={style}>
      {hasToken && !hideLogout && (
        <SupportChatBtn
          type="button"
          aria-label="Support chat"
          onClick={() => Intercom.showChat()}
        >
          <SupportChatIcon />
        </SupportChatBtn>
      )}

      <StepColumn>
        <div style={contentStyle}>
          {title ? <EntryTitle $size={34}>{title}</EntryTitle> : null}
          {subtitle ? <Subtitle style={subtitleStyle}>{subtitle}</Subtitle> : null}
        </div>

        <div style={formStyle}>
          <Children>{children}</Children>
          {subContent}
        </div>

        <div aria-hidden="true" style={{ height: ctaHeight, flexShrink: 0 }} />
        {cta ?? <div aria-hidden="true" style={{ marginTop: "auto" }} />}
      </StepColumn>
    </StepScreen>
  );
};
